import { useState, useCallback, useRef } from 'react';
import warehouseService from '../services/warehouseService';
import { useMessageAlert } from '../components/common/MessageAlert';
import i18n from '../i18n';

/**
 * Hook: useWarehouses
 * - Provides CRUD and list operations for warehouses
 */
const useWarehouses = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const { showCreate, showUpdate, showDelete } = useMessageAlert();

  // Deduplication & in-flight tracking
  const lastSignatureRef = useRef('');
  const lastFetchTimeRef = useRef(0);
  const inFlightRef = useRef(new Set());

  const generateSignature = useCallback((params) => JSON.stringify(params || {}), []);

  const shouldDedupe = useCallback((signature, force) => {
    if (force) return false;
    const now = Date.now();
    const timeDiff = now - lastFetchTimeRef.current;
    return signature === lastSignatureRef.current && timeDiff < 200;
  }, []);

  const fetchWarehouses = useCallback(async (params = {}, options = {}) => {
    const { force = false } = options;
    const signature = generateSignature(params);

    if (shouldDedupe(signature, force)) return { success: true, fromCache: true };
    if (inFlightRef.current.has(signature)) return { success: true, fromCache: true };

    setLoading(true);
    setError(null);
    inFlightRef.current.add(signature);

    try {
      const result = await warehouseService.getAllWarehouses(params, options);
      if (result.success) {
        const data = result.data;
        if (Array.isArray(data)) {
          setWarehouses(data);
          setTotalPages(1);
          setTotalCount(data.length);
        } else if (Array.isArray(data.warehouses)) {
          setWarehouses(data.warehouses);
          setTotalPages(data.totalPages || Math.ceil((data.total || data.warehouses.length) / (params.limit || 20)));
          setTotalCount(data.total || data.warehouses.length);
        } else {
          setWarehouses(data || []);
          setTotalPages(0);
          setTotalCount(Array.isArray(data) ? data.length : 0);
        }

        lastSignatureRef.current = signature;
        lastFetchTimeRef.current = Date.now();
      } else {
        setError(result.error);
        setWarehouses([]);
      }

      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      setWarehouses([]);
      return { success: false, error: errorMessage };
    } finally {
      inFlightRef.current.delete(signature);
      setLoading(false);
    }
  }, [generateSignature, shouldDedupe]);

  const getWarehouseById = useCallback(async (warehouseId, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await warehouseService.getWarehouseById(warehouseId, options);
      if (!result.success) setError(result.error);
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const createWarehouse = useCallback(async (payload) => {
    setError(null);
    try {
      const result = await warehouseService.createWarehouse(payload);
      if (result.success) {
        setWarehouses(prev => [result.data, ...prev]);
        setTotalCount(prev => prev + 1);
        const label = result.data?.id;
        showCreate(i18n.t('warehouses.notifications.created', { id: label }));
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showCreate]);

  const updateWarehouse = useCallback(async (warehouseId, payload) => {
    setError(null);
    try {
      const result = await warehouseService.updateWarehouse(warehouseId, payload);
      if (result.success) {
        setWarehouses(prev => prev.map(w => (String(w.id) === String(warehouseId) ? { ...w, ...result.data } : w)));
        showUpdate(i18n.t('warehouses.notifications.updated', { id: String(warehouseId) }));
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showUpdate]);

  const deleteWarehouse = useCallback(async (warehouseId, options = {}) => {
    setError(null);
    try {
      const result = await warehouseService.deleteWarehouse(warehouseId, options);
      if (result.success) {
        setWarehouses(prev => prev.filter(w => String(w.id) !== String(warehouseId)));
        setTotalCount(prev => Math.max(0, prev - 1));
        showDelete(i18n.t('warehouses.notifications.deleted', { id: String(warehouseId) }));
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showDelete]);

  const bulkDeleteWarehouses = useCallback(async (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return { success: false, error: i18n.t('warehouses.alerts.noSelection') };
    setError(null);
    try {
      const result = await warehouseService.bulkDeleteWarehouses(ids);
      if (result.success) {
        const idSet = new Set(ids.map((id) => String(id)));
        setWarehouses(prev => prev.filter(w => !idSet.has(String(w.id))));
        setTotalCount(prev => Math.max(0, prev - ids.length));
        showDelete(i18n.t('warehouses.notifications.bulkDeleted', { count: String(ids.length) }));
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showDelete]);

  const clearError = useCallback(() => setError(null), []);

  return {
    // State
    warehouses,
    loading,
    error,
    totalPages,
    totalCount,
    // Actions
    fetchWarehouses,
    getWarehouseById,
    createWarehouse,
    updateWarehouse,
    deleteWarehouse,
    bulkDeleteWarehouses,
    clearError,
  };
};

export default useWarehouses;
